import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface ITileSelectOption {
  value: string;
  /** Text or translation key. */
  label: string;
  /** Text or translation key. */
  description?: string;
  /** Text or translation key of a small highlighted tag. */
  tag?: string;
  /** Material icon name. */
  icon?: string;
  /** Image path, used instead of `icon`. */
  iconSrc?: string;
  /** Path of an SVG drawn with `currentColor`; inlined, so it follows the tile colours (also when selected). */
  svgIcon?: string;
  /** CSS background of a picture strip on top of the tile; `tag` is then shown on it as a pill. */
  cover?: string;
  /** Texts or translation keys listed with check marks below the description. */
  features?: string[];
}

export type TileSelectValue = string | string[] | null;

/**
 * Selectable cards with icon, title and description (ControlValueAccessor).
 * Single choice stores a string (clicking the selected tile clears it unless `deselectable` is false),
 * multiple choice stores a string array.
 */
@Component({
  selector: 'tile-select',
  templateUrl: './tile-select.component.html',
  styleUrls: ['./tile-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TileSelectComponent), multi: true }]
})
export class TileSelectComponent implements ControlValueAccessor {
  options = input.required<ITileSelectOption[]>();
  multiple = input<boolean>(false);
  columns = input<2 | 3 | 4>(3);
  /** Horizontal tiles with the icon on the left. */
  compact = input<boolean>(false);
  deselectable = input<boolean>(true);
  invalid = input<boolean>(false);

  protected readonly value = signal<TileSelectValue>(null);
  protected readonly disabled = signal(false);
  /** Tiles with a cover stand in one column on small screens, they are too tall for two. */
  protected readonly hasCovers = computed(() => this.options().some((option) => !!option.cover));
  protected readonly selected = computed(() => {
    const value = this.value();
    return Array.isArray(value) ? value : value ? [value] : [];
  });

  private onChange: (value: TileSelectValue) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: TileSelectValue): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: TileSelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected isSelected(option: ITileSelectOption): boolean {
    return this.selected().includes(option.value);
  }

  protected toggle(option: ITileSelectOption): void {
    if (this.disabled()) return;

    let value: TileSelectValue;
    if (this.multiple()) {
      const selected = this.isSelected(option)
        ? this.selected().filter((item) => item !== option.value)
        : [...this.selected(), option.value];
      value = selected.length ? selected : null;
    } else if (this.isSelected(option)) {
      if (!this.deselectable()) return;
      value = null;
    } else {
      value = option.value;
    }

    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }
}
